import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { publicApi, api, fileUrl, TOKEN_KEY } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import PreviewBanner from "@/components/public/PreviewBanner";
import WatchCard from "@/components/public/WatchCard";
import Reveal from "@/components/public/Reveal";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Gem, Lock, ArrowRight, Crown, Watch, Star } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_HOME_BLOCKS, mergeHomeBlocks, HOME_DRAFT_KEY } from "@/lib/cms";

// Icon keys (from the CMS) -> lucide components for the Trust Highlights strip.
const TRUST_ICONS = { shield: ShieldCheck, gem: Gem, lock: Lock, crown: Crown, watch: Watch, star: Star };

// --- Section renderers -----------------------------------------------------
// NOTE: The JSX/Tailwind below is intentionally IDENTICAL to the original
// hard-coded homepage. Only literal strings and image URLs are now sourced
// from CMS content so that the default content renders pixel-for-pixel.

function HeroBlock({ content, navigate }) {
  return (
    <section className="relative overflow-hidden h-screen min-h-[640px] flex items-end">
      <div className="absolute inset-0">
        <img
          src={fileUrl(content.image)}
          alt={content.image_alt || ""}
          className="w-full h-full object-cover object-[50%_46%] scale-[1.18]"
        />
        {/* even, theme-aware veil so the watch stays visible while text reads */}
        <div className="absolute inset-0" style={{ background: "color-mix(in srgb, var(--bg) 50%, transparent)" }} />
        {/* soft backing where the writing sits (lower third) */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(72% 46% at 50% 82%, color-mix(in srgb, var(--bg) 48%, transparent), transparent 72%)" }} />
      </div>

      <Reveal className="relative w-full max-w-3xl mx-auto px-5 pb-[2vh] text-center">
        <h1 className="font-serif text-[var(--text)] text-[2.3rem] leading-[1.07] md:text-[3.6rem] md:leading-[1.04] tracking-[-0.01em] [text-shadow:0_2px_28px_var(--bg)]">
          {content.line1}
          {content.line2 ? (
            <span className="block mt-1 md:mt-2 italic text-[#e0c693]">{content.line2}</span>
          ) : null}
        </h1>
        {content.subtitle ? (
          <p className="text-[var(--text-2)] mt-6 max-w-xl mx-auto text-base md:text-lg leading-relaxed [text-shadow:0_1px_16px_var(--bg)]">
            {content.subtitle}
          </p>
        ) : null}
        <div className="mt-9 flex justify-center">
          <button
            onClick={() => navigate(content.cta_link || "/watches")}
            data-testid="hero-browse"
            className="group inline-flex items-center gap-3 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold uppercase tracking-[0.15em] text-sm md:text-base px-10 md:px-12 py-4 md:py-5 rounded-none transition-[background-color,transform] duration-200 active:translate-y-px shadow-[0_16px_40px_-14px_rgba(201,168,106,0.6)]"
          >
            {content.cta_label}
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1.5" />
          </button>
        </div>
        {content.secondary_label ? (
          <Link to={content.secondary_link || "/account/login"} className="inline-block mt-5 text-[var(--muted)] hover:text-[var(--text-2)] text-sm tracking-wide transition-colors">
            {content.secondary_label}
          </Link>
        ) : null}
      </Reveal>
    </section>
  );
}

function TrustBlock({ content }) {
  const items = content.items || [];
  return (
    <section className="border-y border-[var(--border-lch)] bg-[var(--bg-2)]">
      <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-3 gap-8">
        {items.map((f, i) => {
          const Icon = TRUST_ICONS[f.icon] || Star;
          return (
            <Reveal key={i} style={{ transitionDelay: `${i * 80}ms` }} className="text-center md:text-left group">
              <Icon className="text-[#c9a86a] mx-auto md:mx-0 transition-transform duration-200 group-hover:-translate-y-0.5" size={26} />
              <div className="text-[var(--text)] font-medium mt-3 font-display text-lg">{f.title}</div>
              <p className="text-sm text-[var(--muted)] mt-1 leading-relaxed">{f.body}</p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function MilitaryBlock({ content }) {
  return (
    <section className="border-b border-[var(--border-lch)] bg-[var(--panel-2)]" data-testid="military-section">
      <Reveal className="max-w-5xl mx-auto px-5 py-16 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
        <div className="shrink-0 h-16 w-16 rounded-full border border-[#c9a86a]/40 flex items-center justify-center text-[#c9a86a]">
          <ShieldCheck size={30} />
        </div>
        <div className="flex-1">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-2">{content.eyebrow}</div>
          <h2 className="text-2xl md:text-3xl text-[var(--text)] font-serif">{content.title}</h2>
          <p className="text-[var(--text-2)] mt-3 max-w-2xl leading-relaxed">{content.body}</p>
        </div>
        <Button
          onClick={() => toast.info("Secure ID.me verification is being connected \u2014 you'll be able to verify and unlock your 10% discount at checkout shortly.")}
          data-testid="idme-verify-btn"
          className="shrink-0 bg-transparent border border-[#c9a86a]/50 hover:border-[#c9a86a] hover:bg-[#c9a86a]/[0.08] text-[var(--text)] rounded-none px-7 py-6 tracking-wide transition-colors"
        >
          <ShieldCheck size={16} className="mr-2 text-[#c9a86a]" /> {content.button_label}
        </Button>
      </Reveal>
    </section>
  );
}

function TopPicksBlock({ content, picks }) {
  if (!picks || picks.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 py-24" data-testid="top-picks-section">
      <Reveal className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">{content.eyebrow}</div>
          <h2 className="text-3xl md:text-4xl text-[var(--text)]">{content.title}</h2>
        </div>
        <Link to="/watches" className="text-sm text-[#c9a86a] hover:text-[#e0c693] whitespace-nowrap transition-colors">{content.view_all_label}</Link>
      </Reveal>
      <Reveal className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {picks.slice(0, 4).map((w) => <WatchCard key={w.id} watch={w} />)}
      </Reveal>
    </section>
  );
}

function VipTiersBlock({ content, tiers, navigate }) {
  return (
    <section className="max-w-6xl mx-auto px-5 py-24">
      <Reveal className="text-center mb-12">
        <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">{content.eyebrow}</div>
        <h2 className="text-3xl md:text-4xl text-[var(--text)]">{content.title}</h2>
        <p className="text-[var(--text-2)] mt-4 max-w-2xl mx-auto leading-relaxed">{content.subtitle}</p>
      </Reveal>
      <Reveal className="grid md:grid-cols-5 gap-4">
        {tiers.map((t, i) => (
          <div key={t.key} className={`border rounded-lg p-5 flex flex-col transition-[transform,border-color] duration-200 hover:-translate-y-1 ${i === tiers.length - 1 ? "border-[#c9a86a] bg-[var(--panel-2)]" : "border-[var(--border-lch)] bg-[var(--panel)] hover:border-[#c9a86a]/40"}`}>
            <div className="text-[10px] text-[var(--muted)] tracking-widest">TIER {t.rank}</div>
            <div className="text-[#c9a86a] font-semibold mt-1 font-display text-lg">{t.name}</div>
            <div className="text-xs text-[var(--text-2)] mt-2 italic">{t.tagline}</div>
            <ul className="mt-4 space-y-1.5 flex-1">
              {t.benefits.map((b) => <li key={b} className="text-xs text-[var(--muted)] flex gap-1.5"><span className="text-[#c9a86a]">{"\u2014"}</span>{b}</li>)}
            </ul>
          </div>
        ))}
      </Reveal>
      <div className="text-center mt-12">
        <Button onClick={() => navigate("/vip")} data-testid="tiers-join-vip"
          className="bg-[#c9a86a] hover:bg-[#b89455] active:translate-y-px text-[var(--on-gold)] font-semibold tracking-wide rounded-none px-8 py-6 transition-[background-color,transform] duration-200">
          {content.cta_label}
        </Button>
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [picks, setPicks] = useState([]);
  const [blocks, setBlocks] = useState(DEFAULT_HOME_BLOCKS);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    publicApi.get("/public/vip-tiers").then((r) => setTiers(r.data)).catch(() => {});
    publicApi.get("/public/top-picks").then((r) => setPicks(r.data)).catch(() => {});

    const loadPublished = () =>
      publicApi
        .get("/public/content/home_builder")
        .then((r) => setBlocks(mergeHomeBlocks(r.data?.blocks)))
        .catch(() => setBlocks(DEFAULT_HOME_BLOCKS));

    // Admin-only draft preview: /?preview=1 renders the unpublished draft when
    // a valid admin token is present. Visitors never see unpublished drafts.
    const wantsPreview = new URLSearchParams(window.location.search).get("preview") === "1";
    const adminToken = localStorage.getItem(TOKEN_KEY);
    if (wantsPreview && adminToken) {
      api
        .get(`/admin/content/${HOME_DRAFT_KEY}`)
        .then((r) => { setBlocks(mergeHomeBlocks(r.data?.blocks)); setPreview(true); })
        .catch(loadPublished);
    } else {
      loadPublished();
    }
  }, []);

  const renderBlock = (block) => {
    if (!block || block.enabled === false) return null;
    const c = block.content || {};
    switch (block.type) {
      case "hero":
        return <HeroBlock key={block.id} content={c} navigate={navigate} />;
      case "trust":
        return <TrustBlock key={block.id} content={c} />;
      case "military":
        return <MilitaryBlock key={block.id} content={c} />;
      case "top_picks":
        return <TopPicksBlock key={block.id} content={c} picks={picks} />;
      case "vip_tiers":
        return <VipTiersBlock key={block.id} content={c} tiers={tiers} navigate={navigate} />;
      default:
        return null;
    }
  };

  return (
    <PublicLayout>
      {blocks.map(renderBlock)}
      {preview && <PreviewBanner testId="home-preview-banner" exitTo="/" />}
    </PublicLayout>
  );
}
